import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FiX, FiSettings, FiAlertTriangle } from "react-icons/fi";
import { fetchScheduleSettings, updateScheduleSettings } from "../../../utils/taskApi";

/**
 * The scheduler's knobs.
 *
 * Two of these are dangerous in a way the others are not, and the form says so
 * rather than relying on the admin to know: `is_enabled` starts the automation
 * for everyone, and `require_running_event` off means publishing tasks whose
 * points nobody can be credited with. Both get a sentence, not just a switch.
 */

const NUMBER_FIELDS = [
  {
    key: "daily_count",
    label: "Tasks per day",
    min: 1,
    max: 20,
    hint: "How many occurrences the generator publishes each day.",
  },
  {
    key: "max_per_type",
    label: "Max per type, per day",
    min: 1,
    max: 20,
    hint: "Three Telegram joins in one day reads as spam; one of each reads as a curated set.",
  },
  {
    key: "generate_days_ahead",
    label: "Build days ahead",
    min: 1,
    max: 30,
    hint: "How far forward the schedule is built, so an empty day is visible before it arrives.",
  },
];

const NULLABLE_FIELDS = [
  {
    key: "daily_point_budget",
    label: "Daily point budget",
    min: 0,
    hint: "Ceiling on reward points schedulable per day. Leave blank for no cap.",
  },
  {
    key: "per_user_daily_cap",
    label: "Per-user daily cap",
    min: 1,
    hint: "Reserved — not enforced yet. Leave blank for no cap.",
  },
];

const ScheduleSettingsModal = ({ isOpen, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchScheduleSettings()
      .then((res) => {
        if (!cancelled) setForm(res.data || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load settings");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      // An empty box means "uncapped", which the API spells `null` — sending
      // "" would be a validation error rather than the clear it looks like.
      const payload = {
        ...form,
        daily_point_budget: form.daily_point_budget === "" ? null : form.daily_point_budget,
        per_user_daily_cap: form.per_user_daily_cap === "" ? null : form.per_user_daily_cap,
      };
      const res = await updateScheduleSettings(payload);
      toast.success("Scheduler settings saved");
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <FiSettings size={16} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">Scheduler Settings</h2>
              <p className="text-[11px] text-gray-400">Changes take effect on the next build.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 transition">
            <FiX size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {loading && <p className="text-sm text-gray-400">Loading settings...</p>}
          {error && !loading && <p className="text-sm text-rose-600">{error}</p>}

          {form && !loading && (
            <>
              <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50/60 transition">
                <input
                  type="checkbox"
                  checked={!!form.is_enabled}
                  onChange={(e) => setField("is_enabled", e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-blue-600"
                />
                <span className="text-sm">
                  <span className="font-semibold text-gray-800">Run the daily scheduler</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Off, nothing is published and the mobile Tasks screen stays empty. On, the pool starts
                    publishing every morning — for everyone.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50/60 transition">
                <input
                  type="checkbox"
                  checked={!!form.require_running_event}
                  onChange={(e) => setField("require_running_event", e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-blue-600"
                />
                <span className="text-sm">
                  <span className="font-semibold text-gray-800">Only publish while an event is running</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Task rewards are event points. With this off, tasks get published between events and every
                    tap on them fails — leave it on unless you know why you are turning it off.
                  </span>
                </span>
              </label>

              {!form.require_running_event && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                  <FiAlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={15} />
                  <p className="text-xs text-amber-800">
                    Tasks published between events cannot be completed — there is no event to credit the points
                    to.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {NUMBER_FIELDS.map((field) => (
                  <div key={field.key}>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">{field.label}</label>
                    <input
                      type="number"
                      min={field.min}
                      max={field.max}
                      value={form[field.key] ?? ""}
                      onChange={(e) => setField(field.key, e.target.value === "" ? "" : Number(e.target.value))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">{field.hint}</p>
                  </div>
                ))}

                {NULLABLE_FIELDS.map((field) => (
                  <div key={field.key}>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">{field.label}</label>
                    <input
                      type="number"
                      min={field.min}
                      placeholder="No cap"
                      value={form[field.key] ?? ""}
                      onChange={(e) => setField(field.key, e.target.value === "" ? "" : Number(e.target.value))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">{field.hint}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 sticky bottom-0 bg-white rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || !form}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSettingsModal;
